import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HardDrive, Play, Loader2, Volume2, VolumeX, Pause, User, Palette } from 'lucide-react';
import { customerApi, Collectiv3Scene } from '../services/customerApi';
import { Viewer } from '@ydrive/gs-viewer';
import SceneDetailsPanel from '../components/SceneDetailsPanel';
import { useAuth } from '../contexts/AuthContext';
import EnterpriseSolutionModal from '../components/EnterpriseSolutionModal';
import PageMasthead from '../components/PageMasthead';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

// Load logo from public folder
const logo = '/logo.gif';

// Function to get camera position based on scene ID
    const getCameraPositionForScene = (sceneId: string): [number, number, number] => {
      switch (sceneId.toLowerCase()) {
        case 'maggie':
          return [0,50,50]; //in front of stairs
        case 'bike':
          return [0, 0, 10];
        case 'kusadak':
          return [0.8, 0, -1]; //in living room
        case 'wingwalker':
          return [-38.32,37.13,14.82];
        case 'ezra':
          return [-1.81,0,-1.19]; //entrance of the galary
        case 'terrebonne':
          return [-3310, 34, 2582];
        case 'drone_construction_site':
          return [-944.59, 275.04, 844.26];
        case 'home_construction':
          return [-1.50, 0.41, -10.32];
        case 'city_segment':
          return [-3310, 34, 2582];
        default:
          return [0, 0, 0];
      }
    };

    const getCameraLookAtForScene = (sceneId: string): [number, number, number] | undefined => {
      switch (sceneId.toLowerCase()) {
        case 'terrebonne':
          return [-6640, 34.3, 5200];
        case 'drone_construction_site':
          return [-1030.2868, 227.3243, 777.0526];
        case 'home_construction':
          return [-1.50, 0.41, 0];
        case 'city_segment':
          return [-6640, 34.3, 5200];
        case 'maggie':
          return undefined;
        case 'ezra':
          return undefined;
        default:
          return undefined;
      }
    };

    const getLoadingVideoForScene = (sceneId: string): string => {
      switch (sceneId.toLowerCase()) {
        case 'maggie':
          return '/Maggie.webm';
        case 'bike':
          return '/Bike.webm';
        case 'kusadak':
          return '/Kusadak.webm'; //in living room
        case 'wingwalker':
          return '/Wing.webm';
        case 'ezra':
          return '/Ezra.webm'; //entrance of the galary
        case 'terrebonne':
          return '/GeoAuto.webm';
        default:
          return '';
      }
    };

const TheCollectiv3: React.FC = () => {
  const navigate = useNavigate();
  const { authenticated } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scenes, setScenes] = useState<Collectiv3Scene[]>([]);

  // ===== 3D VIEWER STATE =====
  const [currentView, setCurrentView] = useState<'gallery' | '3d-viewer'>('gallery');
  const [selectedScene, setSelectedScene] = useState<Collectiv3Scene | undefined>(undefined);
  const [modelLoading, setModelLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const modelContainerRef = React.useRef<HTMLDivElement>(null);
  const progressTextRef = React.useRef<HTMLParagraphElement>(null);
  const viewerRef = React.useRef<any>(null);
  
  // ===== MULTI-PHASE LOADING STATE =====
  const [loadingPhase, setLoadingPhase] = useState<'download' | 'processing' | 'finalizing'>('download');
  const [overallProgress, setOverallProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const [isLargeScene, setIsLargeScene] = useState(false);
  
  // ===== AUDIO STATE =====
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [audioError, setAudioError] = useState(false);

  // ===== ENTERPRISE MODAL STATE =====
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  // Fetch all scenes
  const fetchScenes = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[TheCollectiv3] Fetching all scenes...');
      const response = await customerApi.getAllCollectiv3Scenes();
      
      if (response.success) {
        // Filter out scenes with "marketing" category
        const filteredScenes = response.data.filter(
          scene => scene.category.toLowerCase() !== 'marketing'
        );    
        setScenes(filteredScenes);
        console.log(`[TheCollectiv3] Loaded ${response.data.length} scenes`);
        
        // Debug: Log thumbnail info for each scene
        filteredScenes.forEach((scene, index) => {
          console.log(`[TheCollectiv3] Scene ${index + 1}: ${scene.sceneName}`);
          console.log(`  - Thumbnail: ${scene.thumbnail || 'NOT SET'}`);
          console.log(`  - Category: ${scene.category}`);
        });
      } else {
        throw new Error('Failed to fetch gallery scenes');
      }
    } catch (err) {
      console.error('[TheCollectiv3] Error fetching scenes:', err);
      setError(err instanceof Error ? err.message : 'Failed to load gallery scenes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScenes();
  }, []);

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return <Palette className="w-3 h-3" />;
      case 'people':
        return <User className="w-3 h-3" />;
      default:
        return <HardDrive className="w-3 h-3" />;
    }
  };

  // Get category color
  const getCategoryColor = (category: string) => {
    switch (category.toLowerCase()) {
      case 'art':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'outdoor':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'indoor':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'people':
        return 'bg-orange-500/20 text-orange-300 border-orange-500/30';
      default:
        return 'bg-[#0CB4E4]/20 text-[#0CB4E4] border-[#0CB4E4]/30';
    }
  };



  
  // ===== 3D VIEWER LOGIC =====
  const handleSceneClick = async (scene: Collectiv3Scene) => {
    // Check file size for large scene notification
    try {
      const response = await fetch(scene.gaus, { method: 'HEAD' });
      const contentLength = response.headers.get('content-length');
      if (contentLength) {
        const fileSizeInMB = parseInt(contentLength) / (1024 * 1024);
        setIsLargeScene(fileSizeInMB > 500);
      }
    } catch (error) {
      console.warn('[TheCollectiv3] Could not check file size:', error);
      setIsLargeScene(false);
    }

    try {
    console.log(`[TheCollectiv3] Loading 3D viewer for scene: ${scene.sceneName}`);
    
    // STEP 1: Cleanup any existing viewer
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        viewerRef.current.dispose();
      } catch (error) {
        console.warn('[TheCollectiv3] Error disposing previous viewer:', error);
      }
      viewerRef.current = null;
    }
    
    // STEP 2: Set initial state
    setCurrentView('3d-viewer');
    setSelectedScene(scene);
    setModelLoading(true);
    setLoadingProgress(0);
    setLoadingPhase('download');
    setOverallProgress(0);
    setProcessingStatus('');
    
    // STEP 3: Basic DOM yield
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // STEP 4: Setup loading text
    if (progressTextRef.current) {
      const fullText = scene.loadingDescription || `Loading ${scene.sceneName}...`;
      const sentences = fullText
        .split(/(?<=[.!?])\s+/)
        .filter(sentence => sentence.trim().length > 0)
        .map(sentence => sentence.trim());
      progressTextRef.current.textContent = sentences.join('\n');
    }
    
    // STEP 5: Container check
    if (!modelContainerRef.current) {
      throw new Error('3D viewer container not found in DOM');
    }
    
  

    // STEP 6: Create viewer
    const cameraPosition = getCameraPositionForScene(scene.sceneId);
    const cameraLookAt = getCameraLookAtForScene(scene.sceneId);
    console.log(`[TheCollectiv3] Using camera position:`, cameraPosition);
    console.log(`[TheCollectiv3] Using camera lookAt:`, cameraLookAt);

    const viewerOptions: any = {
      sharedMemoryForWorkers: false,
      rootElement: modelContainerRef.current,
      initialCameraPosition: cameraPosition,
      enableOptimizations: true,
    };

    // Only add initialCameraLookAt if we have a custom one
    if (cameraLookAt) {
      viewerOptions.initialCameraLookAt = cameraLookAt;
    }

    const viewer = new Viewer(viewerOptions);
    viewerRef.current = viewer;
    
    // STEP 7: progress tracking
    let lastProgress = 0;
    let downloadComplete = false;
    let processingStarted = false;
    
    const splatBufferOptions = {
      showLoadingUI: false,  
    
      onProgress: (progress: number) => {
        console.log(`[DEBUG] Viewer progress: ${progress}%`);
        
        if (progress < lastProgress) return;
        lastProgress = progress;
             
        if (progress < 100) {
          // Download phase
          const downloadProgress = Math.round(progress * 0.33); // 0-33%
          
          setLoadingPhase('download');
          setOverallProgress(downloadProgress);
          setLoadingProgress(progress);
          setProcessingStatus(`Downloading: ${Math.round(progress)}%`);
          
          console.log(`[DEBUG] UI updated to ${downloadProgress}% overall`);
          
        } else if (progress === 100 && !downloadComplete) {
          // Processing phase - continue from where download left off
          downloadComplete = true;
          
          setLoadingPhase('processing');
          // DON'T reset to 33% - continue from current progress
          const currentProgress = overallProgress;
          setProcessingStatus('Processing 3D data...');
          
          console.log(`[DEBUG] Switched to processing, continuing from ${currentProgress}%`);
          
          // Start processing simulation from current progress
          if (!processingStarted) {
            processingStarted = true;
            startProcessingSimulation();
          }
        }
      },
      
      // Add environment map if available
      ...(scene.envMap && {
        environmentMapPath: scene.envMap,
        deferEnvironmentMap: true,
      }),
      
      // Add image poses if available
      ...(scene.imagePoses && {
      imagePosesPath: scene.imagePoses,  
      deferImagePoses: true,
    })
  };
    
    // STEP 8: Load scene
    console.log('[TheCollectiv3] Starting scene loading...');
    
    await viewer.addSplatScene(scene.gaus, splatBufferOptions);
    
    // STEP 9: Finalizing
    setLoadingPhase('finalizing');
    setProcessingStatus('Starting 3D viewer...');
    
    // Animate to completion
    let currentProgress = overallProgress;
    const finalize = () => {
      if (currentProgress < 100) {
        currentProgress = Math.min(currentProgress + 5, 100);
        setOverallProgress(currentProgress);
        
        if (currentProgress < 100) {
          setTimeout(finalize, 50);
        } else {
          // Complete
          setModelLoading(false);
          console.log('[TheCollectiv3] Loading complete - scene visible');
          
          // Setup audio immediately when scene is ready
          if (scene.audio) {
            setTimeout(() => {
              setupAudio(scene);
            }, 500);
          }
        }
      }
    };
    finalize();
    
    // STEP 10: Start viewer
    viewer.start();
    
    console.log(`[TheCollectiv3] Scene loaded: ${scene.sceneName}`);
    
  } catch (error) {
    console.error('[TheCollectiv3] Loading error:', error);
    setModelLoading(false);
    setSelectedScene(undefined);
    setCurrentView('gallery');
    
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    alert(`Failed to load 3D viewer: ${errorMessage}`);
  }
};

const startProcessingSimulation = () => {
  console.log('[DEBUG] Starting processing simulation');
  
  let progress = 33;
  const maxProgress = 80;
  
  const update = () => {
    if (progress < maxProgress && modelLoading) {
      progress += Math.random() * 2 + 1;
      const newProgress = Math.min(Math.round(progress), maxProgress);
      
      setOverallProgress(newProgress);
      
      // Update status
      let status = 'Processing 3D data...';
      if (newProgress > 40) status = 'Decompressing...';
      if (newProgress > 55) status = 'Preparing buffers...';
      if (newProgress > 70) status = 'Optimizing...';
      setProcessingStatus(status);
      
      console.log(`[DEBUG] Processing: ${newProgress}%`);
      
      // Continue
      setTimeout(update, Math.random() * 400 + 600);
    }
  };
  
  // Start with small delay
  setTimeout(update, 200);
};

// Add debugging useEffect
useEffect(() => {
  console.log(`[DEBUG] State - Progress: ${overallProgress}%, Phase: ${loadingPhase}, Loading: ${modelLoading}`);
}, [overallProgress, loadingPhase, modelLoading]);

  // Separate audio setup function
  const setupAudio = (scene: Collectiv3Scene) => {
    // Early return if no audio file exists for this scene
    if (!scene.audio) {
      console.log('[TheCollectiv3] No audio file specified for this scene');
      return;
    }
    
    if (!audioRef.current) {
      console.log('[TheCollectiv3] Audio ref not available');
      return;
    }

    try {
      console.log('[TheCollectiv3] Setting up audio separately:', scene.audio);
      
      // Reset audio state
      setAudioError(false);
      setIsAudioPlaying(false);
      
      const audio = audioRef.current;
      const fullAudioUrl = `${window.location.origin}${scene.audio}`;
      
      console.log('[TheCollectiv3] Audio URL:', fullAudioUrl);
      console.log('[TheCollectiv3] Audio element:', audio);
      console.log('[TheCollectiv3] Audio readyState before:', audio.readyState);
      
      // Clean up any existing listeners
      audio.removeEventListener('canplay', autoPlayHandler);
      audio.removeEventListener('error', errorHandler);
      audio.removeEventListener('loadeddata', loadedDataHandler);
      
      // Reset and set source
      audio.pause();
      audio.currentTime = 0;
      audio.src = fullAudioUrl;
      
      console.log('[TheCollectiv3] Audio src set to:', audio.src);
      
      // Define handlers
      function autoPlayHandler() {
        console.log('[TheCollectiv3] Audio canplay event fired');
        console.log('[TheCollectiv3] Audio readyState:', audio.readyState);
        
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsAudioPlaying(true);
              setAudioError(false);
              console.log('[TheCollectiv3] Auto-play started successfully');
            })
            .catch(error => {
              console.warn('[TheCollectiv3] Auto-play failed:', error);
              setIsAudioPlaying(false);
            });
        }
        
        audio.removeEventListener('canplay', autoPlayHandler);
      }
      
      function errorHandler(e: any) {
        console.error('[TheCollectiv3] Audio error:', e);
        console.error('[TheCollectiv3] Audio error details:', audio.error);
        setAudioError(true);
        setIsAudioPlaying(false);
        
        audio.removeEventListener('error', errorHandler);
        audio.removeEventListener('canplay', autoPlayHandler);
      }
      
      function loadedDataHandler() {
        console.log('[TheCollectiv3] Audio loadeddata event fired');
        console.log('[TheCollectiv3] Audio readyState after loadeddata:', audio.readyState);
      }
      
      // Add event listeners
      audio.addEventListener('canplay', autoPlayHandler);
      audio.addEventListener('error', errorHandler);
      audio.addEventListener('loadeddata', loadedDataHandler);
      
      // Force load
      audio.load();
      
      console.log('[TheCollectiv3] Audio load() called');
      
      // Debug: Check if the URL is accessible
      fetch(fullAudioUrl, { method: 'HEAD' })
        .then(response => {
          console.log('[TheCollectiv3] Audio URL accessibility check:', response.status, response.statusText);
        })
        .catch(fetchError => {
          console.error('[TheCollectiv3] Audio URL not accessible:', fetchError);
        });
        
    } catch (error) {
      console.error('[TheCollectiv3] Audio setup error:', error);
      setAudioError(true);
    }
  };

  // Close viewer function
  const close3DViewer = () => {
    console.log(`[TheCollectiv3] Closing 3D viewer`);
    
    // Clean up any running progress intervals
    if ((window as any).progressInterval) {
      clearInterval((window as any).progressInterval);
      (window as any).progressInterval = null;
    }
    
    // Stop and cleanup audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsAudioPlaying(false);
      setAudioError(false);
    }
    
    // Dispose viewer properly
    if (viewerRef.current && viewerRef.current !== 'cleaning') {
      try {
        const viewer = viewerRef.current;
        viewerRef.current = 'cleaning' as any;
        
        if (typeof viewer.dispose === 'function') {
          viewer.dispose();
        }
        console.log('Viewer disposed successfully');
      } catch (error) {
        console.warn('[TheCollectiv3] Error disposing viewer:', error);
      }
      viewerRef.current = null;
    }
    
    // Reset all viewer state
    setSelectedScene(undefined);
    setModelLoading(false);
    setCurrentView('gallery');
    setLoadingPhase('download');
    setOverallProgress(0);
    setProcessingStatus('');
    setIsLargeScene(false);

    
    // Clear any loading text
    if (progressTextRef.current) {
      progressTextRef.current.textContent = '';
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && currentView === '3d-viewer') {
        event.preventDefault();
        event.stopPropagation();
        close3DViewer();
      }
    };

    if (currentView === '3d-viewer') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [currentView]);

  // Component cleanup
  useEffect(() => {
    return () => {
      // Cleanup audio
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      
      // Cleanup 3D viewer
      if (viewerRef.current && viewerRef.current !== 'cleaning') {
        try {
          if (typeof viewerRef.current.dispose === 'function') {
            viewerRef.current.dispose();
          }
        } catch (error) {
          console.warn('Unmount cleanup error (ignoring):', error);
        } finally {
          viewerRef.current = null;
        }
      }
    };
  }, []);

  // ===== AUDIO CONTROL FUNCTIONS =====
  const toggleAudio = () => {
    if (!audioRef.current) {
      console.error('[TheCollectiv3] Audio ref not available');
      return;
    }

    console.log('[TheCollectiv3] Toggle audio, currently playing:', isAudioPlaying);
    
    if (isAudioPlaying) {
      audioRef.current.pause();
      setIsAudioPlaying(false);
      console.log('[TheCollectiv3] Audio paused');
    } else {
      console.log('[TheCollectiv3] Attempting to play audio:', audioRef.current.src);
      
      if (audioRef.current.readyState < 2) {
        console.warn('[TheCollectiv3] Audio not ready to play. ReadyState:', audioRef.current.readyState);
        alert('Audio is still loading. Please wait and try again.');
        return;
      }
      
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsAudioPlaying(true);
            setAudioError(false);
            console.log('[TheCollectiv3] Audio started playing successfully');
          })
          .catch(error => {
            console.error('[TheCollectiv3] Audio play error:', error);
            setAudioError(true);
            setIsAudioPlaying(false);
            
            if (error.name === 'NotSupportedError') {
              alert('Audio format not supported by your browser');
            } else if (error.name === 'NotAllowedError') {
              alert('Audio playback blocked by browser. Please check your audio settings.');
            } else {
              alert(`Audio error: ${error.message}`);
            }
          });
      }
    }
  };

  // ===== RENDER PUBLIC HEADER (for non-authenticated users) =====
  const renderPublicHeader = () => (
    <SiteHeader />
  );

  // ===== RENDER GALLERY VIEW =====
  const renderGalleryView = () => (
    <div className="min-h-screen bg-[#161513]">
      {/* Conditional Header - Show public header if not authenticated, Layout will handle if authenticated */}
      {!authenticated && renderPublicHeader()}
      {/* Header */}
      <div className="relative bg-[#161513] border-b border-[#0CB4E4]/20">
        {/* Logo Background */}
        <div 
          className="absolute top-0 left-0 right-0 h-full bg-cover bg-center bg-no-repeat opacity-10 z-0 pointer-events-none"
          style={{ backgroundImage: `url(${logo})` }}
        />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 relative z-10">
        {/* Back button - only show for authenticated users */}
        {authenticated && (
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back
            </button>
          </div>
        )}

        {/* Left-aligned and at the shared size, so the four public pages read
            as one site. It was centred at text-6xl bold, which put a third of a
            laptop screen between the header and the first scene card. */}
        <PageMasthead
          title="Gallery"
          tagline="A curated showcase of 3D spatial models created with Reco."
        />
      </div>
    </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Loading State */}
        {loading && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#0CB4E4] mx-auto mb-4" />
            <h3 className="text-title font-medium text-white mb-2">Loading Gallery...</h3>
            <p className="text-gray-300">Preparing our 3D collection for you</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-900/20 text-red-400 p-6 rounded-xl border border-red-500/30 mb-8">
            <h3 className="font-medium mb-2">Unable to Load Gallery</h3>
            <p>{error}</p>
            <button 
              onClick={fetchScenes}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* All Scenes Grid */}
        {!loading && !error && scenes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {scenes.map((scene) => (
              <div
                key={`${scene.sceneName}-${scene.category}`}
                onClick={() => handleSceneClick(scene)}
                className="relative overflow-hidden h-[480px] bg-[#161513] shadow-lg border border-[#0CB4E4]/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] rounded-xl group cursor-pointer"
              >
                {/* Scene Preview */}
                <div className="absolute inset-0 w-full h-full bg-[#161513] overflow-hidden rounded-xl">
                  <div className="h-1/2 bg-gradient-to-br from-[#0CB4E4]/20 via-[#0CB4E4]/10 to-[#0CB4E4]/20 relative flex items-center justify-center">
                    {/* Thumbnail or Logo */}
                    {scene.thumbnail ? (
                      <img 
                        src={scene.thumbnail} 
                        alt={scene.sceneName}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                        onError={(e) => {
                          // Fallback to logo if thumbnail fails to load
                          console.error(`[TheCollectiv3] Thumbnail failed to load: ${scene.thumbnail} for scene: ${scene.sceneName}`);
                          (e.target as HTMLImageElement).src = logo;
                          (e.target as HTMLImageElement).className = "h-24 object-contain transition-transform duration-500 ease-out group-hover:scale-110 opacity-60";
                        }}
                        onLoad={() => {
                          console.log(`[TheCollectiv3] Thumbnail loaded successfully: ${scene.thumbnail} for scene: ${scene.sceneName}`);
                        }}
                      />
                    ) : (
                      <>
                        {console.log(`[TheCollectiv3] No thumbnail found for scene: ${scene.sceneName}, using logo fallback`)}
                        <img 
                          src={logo} 
                          alt="3D Scene Preview" 
                          className="h-24 object-contain transition-transform duration-500 ease-out group-hover:scale-110 opacity-60" 
                        />
                      </>
                    )}
                    
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-[#0CB4E4] rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-[#161513] ml-1" />
                      </div>
                    </div>

                    {/* Category badge */}
                    <div className={`absolute top-3 right-3 backdrop-blur-sm px-3 py-1 text-ui font-medium border rounded-full flex items-center gap-1 ${getCategoryColor(scene.category)}`}>
                      {getCategoryIcon(scene.category)}
                      <span>{scene.category}</span>
                    </div>
                  </div>
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#161513] via-transparent to-transparent" />
                </div>

                {/* Scene Info - Enhanced with cardDescription and credits */}
                <div className="absolute bottom-0 left-0 right-0 p-5 z-10 bg-gradient-to-t from-[#161513] via-[#161513]/95 to-transparent min-h-[240px] flex flex-col justify-end">
                  <h3 className="font-semibold text-white text-body mb-2 line-clamp-1" title={scene.sceneName}>
                    {scene.sceneName}
                  </h3>
                  
                  {/* Card Description */}
                  <p className="text-ui text-gray-300 line-clamp-3 mb-3" title={scene.cardDescription}>
                    {scene.cardDescription}
                  </p>

                  {/* Credits - Show artist tag if category is Art */}
                  {scene.credits && (
                    <div className="mb-3">
                      <span className="text-ui text-gray-400">
                        {scene.category.toLowerCase() === 'art' ? 'Artist: ' : 'Credits: '}
                      </span>
                      <span className="text-ui text-[#0CB4E4] font-medium">
                        {scene.credits}
                      </span>
                    </div>
                  )}

                  {/* Location */}
                  {scene.location && (
                    <div className="mb-3">
                      <span className="text-ui text-gray-400">Location: </span>
                      <span className="text-ui text-gray-300 font-medium">
                        {scene.location}
                      </span>
                    </div>
                  )}

                  {/* Scene Stats */}
                  <div className="flex items-center justify-between text-ui text-gray-400">
                    <div className="flex items-center gap-1">
                      <HardDrive className="w-3 h-3" />
                      <span>3D Scene</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Play className="w-3 h-3" />
                      <span>Interactive</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && scenes.length === 0 && (
          <div className="text-center py-20">
            <img 
              src={logo} 
              alt="No scenes" 
              className="w-16 h-16 mx-auto mb-4 object-contain opacity-50" 
            />
            <h3 className="text-title font-medium text-white mb-2">No Scenes Available</h3>
            <p className="text-gray-300">
              The gallery collection is being prepared. Check back soon!
            </p>
          </div>
        )}
      </div>
    </div>
  );

  // ===== RENDER MULTI-PHASE LOADING UI =====
  const loadingVideoSrc = selectedScene ? getLoadingVideoForScene(selectedScene.sceneId) : '';
  const renderLoadingUI = () => (
  <div className="fixed inset-0 z-[1002]">
    {/* Background video */}
    <video
      key="loading-bg-video"
      src={loadingVideoSrc}
      className="absolute inset-0 w-full h-full object-cover"
      autoPlay
      muted
      loop
      playsInline
    />

    {/* Subtle vignette for readability */}
    <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/20 pointer-events-none" />

    {/* Positioned, elegant glass panel */}
    <div className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[32rem] max-w-[92%]">
      {/* Gradient border wrapper */}
      <div className="p-[1.5px] rounded-3xl bg-gradient-to-br from-white/25 via-[#0CB4E4]/40 to-transparent shadow-2xl">
        <aside className="relative rounded-3xl bg-[#0E0D0C]/[0.15] backdrop-blur-xl border border-white/10 overflow-hidden">
          {/* Close Button */}
          <button
            onClick={close3DViewer}
            className="absolute top-4 right-4 z-10 w-8 h-8 grid place-items-center bg-white/10 hover:bg-white/20 rounded-full text-white/60 hover:text-white transition-colors"
            aria-label="Close viewer"
          >
            <span className="text-title font-light">✕</span>
          </button>
          
          {/* Header */}
          <div className="px-6 pt-5 pb-3 border-b border-white/10">
            <h2 className="text-white text-title font-semibold tracking-tight truncate pr-8">
              {selectedScene?.sceneName || 'Loading Scene'}
            </h2>
          </div>

          {/* Body */}
          <div className="px-6 py-4 space-y-5">
            {/* Description (scrollable if long) */}
            <div className="max-h-40 overflow-y-auto pr-1">
              <p
                ref={progressTextRef}
                className="text-white/90 text-body leading-6 whitespace-pre-wrap break-words"
                style={{
                  fontFamily: "'Source Sans Pro', 'Helvetica Neue', Arial, sans-serif",
                }}
              >
                {selectedScene?.loadingDescription}
              </p>
            </div>

            {/* Progress */}
            <div className="space-y-3">
              {/* Bar with inline % */}
              <div className="relative w-full h-3.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0CB4E4] to-[#0CB4E4]/85 transition-all duration-300 ease-out"
                  style={{ width: `${overallProgress}%` }}
                />
                <div className="absolute inset-0 grid place-items-center">
                  <span className="text-ui font-medium text-white/90 drop-shadow">
                    {overallProgress}% Complete
                  </span>
                </div>
              </div>
              {/* Large scene warning message */}
              {isLargeScene && (
                <div className="text-center space-y-2">
                  <p className="text-ui text-[#0CB4E4] bg-[#0CB4E4]/10 px-3 py-2 rounded-lg border border-[#0CB4E4]/30">
                    Please wait..Loading
                  </p>
                  <div className="flex items-center justify-center gap-2 text-ui text-[#0CB4E4] bg-[#0CB4E4]/10 px-3 py-2 rounded-lg border border-[#0CB4E4]/30">
                    <svg className="w-4 h-4 text-[#0CB4E4] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>If you see a 'Page Unresponsive' error, please click <strong>Wait</strong>.</span>
                  </div>
                </div>
              )}
              {/* Phase chips */}
              <div className="flex items-center justify-between text-ui">
                <PhaseChip
                  active={loadingPhase === 'download'}
                  done={overallProgress > 33}
                  label="Download"
                />
                <PhaseChip
                  active={loadingPhase === 'processing'}
                  done={overallProgress > 80}
                  label="Processing"
                />
                <PhaseChip
                  active={loadingPhase === 'finalizing'}
                  done={overallProgress === 100}
                  label="Finalizing"
                />
              </div>
            </div>
          </div>

          {/* Footer tagline */}
          <div className="px-6 py-4 border-t border-white/10 text-center">
            <p className="text-white/95 text-ui tracking-wide">
              Experience it like you are there!
            </p>
          </div>
        </aside>
      </div>
    </div>
  </div>
);

  // ===== RENDER 3D VIEWER =====
  const render3DViewer = () => (
    <div className="fixed inset-0 w-full h-full bg-black z-[999]">
      {/* Close button */}
      <div className="fixed top-4 right-4 z-[1001]">
        <button
          onClick={close3DViewer}
          className="bg-[#161513]/90 backdrop-blur-sm text-white px-6 py-3 shadow-lg hover:bg-[#161513] transition-all duration-200 flex items-center gap-3 font-medium border border-[#0CB4E4]/30 rounded-xl"
        >
          <span className="text-title">✕</span>
          <span>Close</span>
          <span className="text-ui text-gray-400">(ESC)</span>
        </button>
      </div>

      {/* Brand overlay */}
      <div className="fixed top-4 left-4 z-[1001] flex items-center gap-2">
        <img src={logo} alt="CR3ATE.AI" className="h-10 w-10 object-contain" />
        <h1 className="font-light text-title text-[#0CB4E4] font-brand">
          CR3ATE.AI
        </h1>
      </div>

      {/* Multi-phase Loading UI */}
      {modelLoading && renderLoadingUI()}

      {/* 3D Model Viewer Container */}
      <div
        ref={modelContainerRef}
        className="absolute inset-0 w-full h-full"
        style={{
          zIndex: 1000,
          backgroundColor: "black"
        }}
      />

      {/* Scene Details Panel - Shows sceneDescription when loaded */}
      {selectedScene && !modelLoading && (  
        <SceneDetailsPanel 
          model={{
            fileName: selectedScene.sceneName,
            description: selectedScene.sceneDescription,
            fileType: 'gaus',
            category: selectedScene.category,
            credits: selectedScene.credits,
            location: selectedScene.location,
            KnowMore: selectedScene.KnowMore,
            createdAt: selectedScene.createdAt,
          }}
          onClose={close3DViewer}
        />
      )}

      {/* Audio Controls */}
      {!modelLoading && selectedScene && selectedScene.audio && !audioError && (
        <>
          {/* Hidden Audio Element */}
          <audio
            ref={audioRef}
            loop={true}
            onEnded={() => {
              setIsAudioPlaying(false);
              console.log('[TheCollectiv3] Audio ended');
            }}
            onError={(e) => {
              console.error('[TheCollectiv3] Audio error event:', e);
              setAudioError(true);
              setIsAudioPlaying(false);
            }}
            onCanPlay={() => {
              console.log('[TheCollectiv3] Audio can play - ready to use!');
              setAudioError(false);
            }}
            preload="auto"
          />
          
          {/* Audio Control Button */}
          <div className="fixed bottom-4 left-4 z-[1001]">
            <button
              onClick={toggleAudio}
              className="bg-[#161513]/90 backdrop-blur-sm text-white p-4 shadow-lg hover:bg-[#161513] transition-all duration-200 rounded-xl border border-[#0CB4E4]/30 flex items-center gap-3"
              title={isAudioPlaying ? 'Pause artist narration' : 'Play artist narration'}
            >
              {audioError ? (
                <VolumeX className="w-6 h-6 text-red-400" />
              ) : isAudioPlaying ? (
                <Pause className="w-6 h-6 text-[#0CB4E4]" />
              ) : (
                <Volume2 className="w-6 h-6 text-[#0CB4E4]" />
              )}
              <div className="text-ui">
                <div className="font-medium">Artist Narration</div>
                <div className="text-ui text-gray-400">
                  {audioError ? 'Audio Error' : isAudioPlaying ? 'Playing' : 'Click to play'}
                </div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
  const PhaseChip: React.FC<{ active?: boolean; done?: boolean; label: string }> = ({
  active,
  done,
  label
}) => {
  const base =
    "inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-ui";
  if (done) {
    return (
      <span className={`${base} border-white/15 text-white/70 bg-white/5`}>
        <svg className="w-4 h-4 text-white/60" viewBox="0 0 24 24" fill="none">
          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {label}
      </span>
    );
  }
  if (active) {
    return (
      <span className={`${base} border-[#0CB4E4]/40 text-[#0CB4E4] bg-[#0CB4E4]/10`}>
        <span className="w-2 h-2 rounded-full bg-[#0CB4E4] animate-pulse" />
        {label}
      </span>
    );
  }
  return (
    <span className={`${base} border-white/10 text-white/50 bg-white/5`}>
      <span className="w-2 h-2 rounded-full bg-white/30" />
      {label}
    </span>
  );
};

  // ===== MAIN RENDER =====
  return (
    <>
      {currentView === 'gallery' && renderGalleryView()}
      {currentView === '3d-viewer' && render3DViewer()}
      {/* Enterprise Solution Modal */}
      <EnterpriseSolutionModal
        isOpen={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
      />
      <SiteFooter />
    </>
  );
};

export default TheCollectiv3;